import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Newspaper, Images, Inbox, SlidersHorizontal, FileStack, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArticlesTab } from "@/pages/admin/ArticlesTab";
import { PhotosTab } from "@/pages/admin/PhotosTab";
import { EnquiriesTab } from "@/pages/admin/EnquiriesTab";
import { SiteContentTab } from "@/pages/admin/SiteContentTab";
import { MediaTab } from "@/pages/admin/MediaTab";
import { AccountTab } from "@/pages/admin/AccountTab";

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("articles");

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadStats();
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("ekal_token");
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0C10] flex items-center justify-center">
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-500" data-testid="admin-loading">
          Opening the desk…
        </p>
      </div>
    );
  }

  const statCards = [
    { label: "Articles live", value: stats?.articles ?? "—" },
    { label: "Frames in library", value: stats?.photos ?? "—" },
    { label: "New enquiries", value: stats?.enquiries_new ?? "—" },
    { label: "Total enquiries", value: stats?.enquiries_total ?? "—" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0C10] text-slate-100">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0C10]/85 border-b border-white/10">
        <div className="px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF3B30]" />
            </span>
            <span className="font-heading font-extrabold text-lg uppercase tracking-tight">
              Command Center
            </span>
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
              / {user.email}
            </span>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400 hover:text-[#FF3B30] transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="px-4 sm:px-8 py-8 max-w-6xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {statCards.map((s) => (
            <div key={s.label} className="border border-white/10 bg-[#12151C] p-5" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="font-heading font-extrabold text-3xl text-slate-50">{s.value}</p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); loadStats(); }}>
          <TabsList className="bg-[#12151C] border border-white/10 rounded-none h-11">
            <TabsTrigger data-testid="admin-tab-articles" value="articles" className="rounded-none font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">
              <Newspaper size={14} className="mr-2" /> Articles
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-photos" value="photos" className="rounded-none font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">
              <Images size={14} className="mr-2" /> Photo Library
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-media" value="media" className="rounded-none font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">
              <FileStack size={14} className="mr-2" /> Media
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-enquiries" value="enquiries" className="rounded-none font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">
              <Inbox size={14} className="mr-2" /> Enquiries
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-site-content" value="site-content" className="rounded-none font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">
              <SlidersHorizontal size={14} className="mr-2" /> Site Content
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-account" value="account" className="rounded-none font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">
              <KeyRound size={14} className="mr-2" /> Account
            </TabsTrigger>
          </TabsList>
          <TabsContent value="articles" className="mt-6">
            <ArticlesTab />
          </TabsContent>
          <TabsContent value="photos" className="mt-6">
            <PhotosTab />
          </TabsContent>
          <TabsContent value="media" className="mt-6">
            <MediaTab />
          </TabsContent>
          <TabsContent value="enquiries" className="mt-6">
            <EnquiriesTab />
          </TabsContent>
          <TabsContent value="site-content" className="mt-6">
            <SiteContentTab />
          </TabsContent>
          <TabsContent value="account" className="mt-6">
            <AccountTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
